import { useEffect, useRef, useState } from "react";
import { X, Upload, Download, Plus } from "lucide-react";
import { http, API } from "./api";

// Purge a saved autosave draft (call after a successful save to prevent
// stale drafts from re-appearing on next modal open).
export function purgeDraft(autosaveKey) {
  if (!autosaveKey) return;
  try { localStorage.removeItem(`ward7crm.draft.${autosaveKey}`); } catch { /* noop */ }
}

// Emit a global "open person profile" event — any Shell listener switches to
// the profile page with the given contact_id in pageContext. Use `<PersonLink/>`
// or call `openPerson(id, fallback)` to backlink a name anywhere in the app.
export function openPerson(contactId, fallbackName) {
  if (!contactId) return;
  try {
    window.dispatchEvent(new CustomEvent("wardcrm:open-person", {
      detail: { contact_id: contactId, name: fallbackName || "" },
    }));
  } catch { /* noop */ }
}

export function PersonLink({ contactId, name, className, children, style, testid }) {
  const label = children || name;
  if (!contactId) {
    return <span className={className} style={style} data-testid={testid}>{label}</span>;
  }
  return (
    <button
      type="button"
      className={`person-link ${className || ""}`}
      style={style}
      data-testid={testid || `person-link-${contactId}`}
      onClick={(e) => { e.stopPropagation(); openPerson(contactId, name); }}
      title={`Open ${name || "profile"}`}
    >
      {label}
    </button>
  );
}

// ---------- PersonAutocomplete -----------------------------------------------
// Debounced typeahead over /api/contacts/autocomplete (default) or a caller-
// supplied endpoint. Renders name + photo + booth suggestions in a floating
// dropdown. Calls `onSelect(row)` when a row is picked; also fires `onChange`
// on every keystroke so the parent can bind an uncontrolled/controlled input.
export function PersonAutocomplete({
  value = "",
  onChange,
  onSelect,
  placeholder = "Type a name…",
  testid = "person-autocomplete",
  disabled = false,
  source = "/contacts/autocomplete",
  minLen = 1,
}) {
  const [q, setQ] = useState(value || "");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const term = (q || "").trim();
    if (!term || term.length < minLen) { setRows([]); return undefined; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await http.get(`${source}?q=${encodeURIComponent(term)}&limit=8`);
        setRows(r.data.results || []);
      } catch { setRows([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, source, minLen]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (r) => {
    setQ(r.name || "");
    setOpen(false);
    setActive(-1);
    if (onChange) onChange(r.name || "");
    if (onSelect) onSelect(r);
  };

  const onKey = (e) => {
    if (!open || rows.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(rows.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(rows[active]); }
    else if (e.key === "Escape") { setOpen(false); setActive(-1); }
  };

  return (
    <div className="p-autocomplete" ref={wrapRef} data-testid={`${testid}-wrap`}>
      <input
        type="text"
        value={q}
        disabled={disabled}
        data-testid={`${testid}-input`}
        placeholder={placeholder}
        onChange={(e) => { setQ(e.target.value); if (onChange) onChange(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => { if ((q || "").trim().length >= 1) setOpen(true); }}
        onKeyDown={onKey}
        autoComplete="off"
      />
      {open && (loading || rows.length > 0) && (
        <div className="p-autocomplete-menu" data-testid={`${testid}-menu`}>
          {loading && rows.length === 0 ? (
            <div className="p-autocomplete-empty">Searching…</div>
          ) : rows.length === 0 ? (
            <div className="p-autocomplete-empty">No matches</div>
          ) : rows.map((r, i) => (
            <button
              key={r.contact_id || r.volunteer_id || `${r.name}-${i}`}
              type="button"
              className={`p-autocomplete-row ${i === active ? "on" : ""}`}
              data-testid={`${testid}-row-${i}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(r)}
            >
              {r.profile_photo ? (
                <img src={`${API}/files/${r.profile_photo}`} alt="" className="p-autocomplete-avatar" />
              ) : (
                <span className="p-autocomplete-avatar p-autocomplete-avatar-fallback">
                  {(r.name || "?").trim().charAt(0).toUpperCase()}
                </span>
              )}
              <span className="p-autocomplete-body">
                <b>{r.name}</b>
                <small>
                  {r.booth ? `Booth ${r.booth}` : "No booth"}
                  {r.role ? ` · ${r.role}` : ""}
                  {r.mobile ? ` · ${r.mobile}` : ""}
                </small>
              </span>
              {r.open_complaints_count !== undefined && r.open_complaints_count > 0 && (
                <span
                  className={`p-autocomplete-workload ${r.open_complaints_count > 5 ? "hot" : r.open_complaints_count > 2 ? "warm" : "cool"}`}
                  data-testid={`${testid}-workload-${i}`}
                  title={`${r.open_complaints_count} open complaints assigned`}
                >
                  {r.open_complaints_count} open
                </span>
              )}
              {r.open_complaints_count === 0 && (
                <span className="p-autocomplete-workload free" data-testid={`${testid}-workload-${i}`} title="No open complaints — free hand">
                  Free
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Stack of open modal onClose handlers — only the topmost one reacts to Escape.
const _modalStack = [];
if (typeof document !== "undefined" && !window.__wardCrmModalKeydown) {
  window.__wardCrmModalKeydown = true;
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && _modalStack.length) {
      const entry = _modalStack[_modalStack.length - 1];
      if (entry.isDirty && entry.isDirty()) {
        if (!window.confirm("You have unsaved changes. Discard and close?")) return;
      }
      if (entry.onClose) entry.onClose();
    }
  });
}

export function Modal({ title, onClose, children, testid, isDirty, autosaveKey, getState, applyState }) {
  // Refs so the setInterval below always reads the freshest props (avoids the
  // classic "captured at mount" stale closure that killed autosave writes).
  const isDirtyRef = useRef(isDirty);
  const getStateRef = useRef(getState);
  const applyStateRef = useRef(applyState);
  isDirtyRef.current = isDirty;
  getStateRef.current = getState;
  applyStateRef.current = applyState;

  useEffect(() => {
    const entry = { onClose, isDirty };
    _modalStack.push(entry);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      const idx = _modalStack.lastIndexOf(entry);
      if (idx >= 0) _modalStack.splice(idx, 1);
      if (_modalStack.length === 0) document.body.style.overflow = prev;
    };
  }, [onClose, isDirty]);

  // Draft autosave — restore on mount, persist every 5s while dirty
  useEffect(() => {
    if (!autosaveKey || !getStateRef.current) return undefined;
    if (applyStateRef.current) {
      try {
        const raw = localStorage.getItem(`ward7crm.draft.${autosaveKey}`);
        if (raw) applyStateRef.current(JSON.parse(raw));
      } catch { /* noop */ }
    }
    const key = `ward7crm.draft.${autosaveKey}`;
    const iv = setInterval(() => {
      try {
        const dirtyFn = isDirtyRef.current;
        const stateFn = getStateRef.current;
        if (dirtyFn && dirtyFn() && stateFn) {
          localStorage.setItem(key, JSON.stringify(stateFn()));
        }
      } catch { /* noop */ }
    }, 5000);
    return () => clearInterval(iv);
  }, [autosaveKey]);

  const requestClose = () => {
    if (isDirty && isDirty() && !window.confirm("You have unsaved changes. Discard and close?")) return;
    if (autosaveKey) {
      try { localStorage.removeItem(`ward7crm.draft.${autosaveKey}`); } catch { /* noop */ }
    }
    onClose && onClose();
  };

  const backdropClick = (e) => {
    if (e.target !== e.currentTarget || !onClose) return;
    requestClose();
  };

  return (
    <div className="modal-backdrop" onClick={backdropClick} data-testid={testid ? `${testid}-backdrop` : "modal-backdrop"}>
      <div className="form-modal" data-testid={testid || "modal"} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button data-testid="form-close-button" onClick={requestClose}><X /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <section className="welcome">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}<span>.</span></h1>
        {subtitle && <p className="sub">{subtitle}</p>}
      </div>
      {actions && <div className="welcome-actions">{actions}</div>}
    </section>
  );
}

export function Pill({ tone, children, testid }) {
  return <span className={`pill ${tone || "blue"}`} data-testid={testid}>{children}</span>;
}

export function ImportExportBar({ entity, onImported }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const doExport = () => {
    const url = `${API}/export/${entity}`;
    window.open(url, "_blank");
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setMsg("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await http.post(`/import/${entity}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg(`Imported ${r.data.imported}, updated ${r.data.updated}`);
      if (onImported) onImported();
    } catch {
      setMsg("Import failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="io-bar">
      <label className="outline" data-testid={`${entity}-import-button`}>
        <Upload size={16} /> Import CSV
        <input type="file" accept=".csv" hidden onChange={doImport} disabled={busy} data-testid={`${entity}-import-input`} />
      </label>
      <button className="outline" data-testid={`${entity}-export-button`} onClick={doExport}>
        <Download size={16} /> Export CSV
      </button>
      {msg && <small className="io-msg" data-testid={`${entity}-import-msg`}>{msg}</small>}
    </div>
  );
}

export function AddButton({ onClick, label, testid }) {
  return (
    <button className="primary" data-testid={testid} onClick={onClick}>
      <Plus size={17} />{label}
    </button>
  );
}

export function FormRow({ label, children }) {
  return <label className="frow">{label}{children}</label>;
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="empty-workspace">
      <div className="empty-icon"><Icon /></div>
      <h2>{title}</h2>
      {hint && <p>{hint}</p>}
    </div>
  );
}


/* --------------------------------------------------------------------
 * Undo Toast (Phase 17)
 * A tiny module-level pub/sub so any page can trigger a bottom-left
 * "Undo · 6s" toast after calling a soft-delete DELETE endpoint.
 * ------------------------------------------------------------------ */
const _toastListeners = new Set();
export function showUndoToast(payload) {
  // payload: { label, onUndo, duration=6000 }
  _toastListeners.forEach((l) => l(payload));
}
export function subscribeUndoToast(listener) {
  _toastListeners.add(listener);
  return () => _toastListeners.delete(listener);
}

export function UndoToast() {
  const [toast, setToast] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef(null);
  const tickRef = useRef(null);

  useEffect(() => {
    return subscribeUndoToast((payload) => {
      clearTimeout(timerRef.current);
      clearInterval(tickRef.current);
      const duration = payload.duration || 6000;
      setToast(payload);
      setRemaining(Math.ceil(duration / 1000));
      const start = Date.now();
      tickRef.current = setInterval(() => {
        const left = Math.max(0, duration - (Date.now() - start));
        setRemaining(Math.ceil(left / 1000));
        if (left <= 0) { clearInterval(tickRef.current); }
      }, 200);
      timerRef.current = setTimeout(() => {
        setToast(null); clearInterval(tickRef.current);
      }, duration);
    });
  }, []);

  if (!toast) return null;
  const undo = async () => {
    clearTimeout(timerRef.current);
    clearInterval(tickRef.current);
    const t = toast;
    setToast(null);
    try { await t.onUndo?.(); } catch (e) { /* noop */ }
  };
  const dismiss = () => {
    clearTimeout(timerRef.current);
    clearInterval(tickRef.current);
    setToast(null);
  };

  return (
    <div className="undo-toast" data-testid="undo-toast" role="status">
      <div className="undo-toast-body">
        <b>{toast.label || "Item moved to recycle bin"}</b>
        <small>{toast.hint || "You can undo for a few seconds."}</small>
      </div>
      <div className="undo-toast-actions">
        {toast.onUndo && <button className="undo-toast-btn" data-testid="undo-toast-undo" onClick={undo}>Undo · {remaining}s</button>}
        <button className="undo-toast-dismiss" data-testid="undo-toast-dismiss" onClick={dismiss} title="Dismiss"><X size={13} /></button>
      </div>
    </div>
  );
}

